// Generic segmented-mask-input helpers (keystroke resolution + rendering), shared by
// DatePicker, DateTimePicker, and TimePickerField. No date/time business logic here.

export type SegmentDef = { length: number; min: number; max: number };

export const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

export function padDigits(value: number, length: number) {
  return String(value).padStart(length, '0');
}

export function clampNumber(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

export function applyDigitToSegment(
  current: string,
  digit: string,
  def: SegmentDef
) {
  const next = current + digit;

  if (next.length >= def.length) {
    return {
      value: padDigits(clampNumber(Number(next), def.min, def.max), def.length),
      complete: true,
    };
  }

  const minCandidate = Number(next.padEnd(def.length, '0'));
  const maxCandidate = Number(next.padEnd(def.length, '9'));
  if (maxCandidate >= def.min && minCandidate <= def.max) {
    return { value: next, complete: false };
  }

  const soloValue = Number(next);
  if (soloValue >= def.min && soloValue <= def.max) {
    return { value: padDigits(soloValue, def.length), complete: true };
  }

  return undefined;
}

// Month names typed as letters (e.g. "F", "Feb", "February") narrow down to a single
// 1-12 candidate as soon as the typed prefix is unambiguous.
export function matchMonthCandidates(buffer: string): number[] {
  const lower = buffer.toLowerCase();
  return MONTH_NAMES.reduce<number[]>((matches, name, index) => {
    if (name.toLowerCase().startsWith(lower)) matches.push(index + 1);
    return matches;
  }, []);
}

// Resolves what the month segment should display: the in-progress letter buffer while
// typing a name, the full month name once resolved, or the "--" placeholder.
export function resolveMonthDisplay(
  monthValue: string,
  monthTextBuffer: string
): { resolved: boolean; displayText: string } {
  const resolved = monthValue.length === 2 && !monthTextBuffer;
  const displayText = monthTextBuffer
    ? monthTextBuffer
    : resolved
      ? MONTH_NAMES[Number(monthValue) - 1]
      : '--';
  return { resolved, displayText };
}

// Applied to a shared <Input> to shrink it down to a compact, borderless mask segment.
export const SEGMENT_INPUT_CLASS_NAME =
  'tw:h-auto tw:w-auto tw:min-w-0 tw:flex-none tw:shrink-0 tw:rounded-none tw:border-0 tw:bg-transparent tw:p-0 tw:text-center tw:text-sm tw:tabular-nums tw:text-foreground tw:shadow-none tw:outline-none tw:focus-visible:shadow-none tw:disabled:cursor-not-allowed tw:disabled:opacity-50';

// 'ch' is sized off the digit "0" glyph, so pad with a px buffer to avoid clipping wider glyphs (e.g. "AM"/"PM").
export function segmentWidthPx(length: number) {
  return `${length * 9 + 3}px`;
}

// True once any segment (or an in-progress month-name buffer) has been typed, used to
// decide when a field's clear button should appear.
export function hasAnySegmentValue(
  segments: Record<string, string>,
  extraBuffer?: string
): boolean {
  return (
    Boolean(extraBuffer) ||
    Object.values(segments).some((value) => value !== '')
  );
}

// Keys that should keep their default browser behavior (tabbing, shortcuts, etc.)
// instead of being blocked by a segment input's "stray printable character" guard.
export const SEGMENT_NAVIGATION_ALLOWED_KEYS = [
  'Tab',
  'Shift',
  'Meta',
  'Control',
  'Alt',
  'Enter',
  'Escape',
];

export function getAdjacentSegmentKey<T extends string>(
  order: T[],
  key: T,
  direction: -1 | 1
): T | undefined {
  const index = order.indexOf(key) + direction;
  return index >= 0 && index < order.length ? order[index] : undefined;
}

export type SegmentKeyAction =
  | { type: 'digit'; value: string; complete: boolean }
  | { type: 'backspaceValue'; value: string }
  | { type: 'backspaceRetreat' }
  | { type: 'clear' }
  | { type: 'retreat' }
  | { type: 'advance' }
  | { type: 'ignore' }
  | { type: 'block' };

// Resolves what a keystroke should do to a purely-numeric segment (day/year/hour/
// minute/second), leaving the caller to apply the resulting state change, focus
// movement, and event.preventDefault().
export function resolveNumericSegmentKeyAction(params: {
  key: string;
  currentValue: string;
  isFresh: boolean;
  def: SegmentDef;
  advanceKeys: string[];
}): SegmentKeyAction {
  const { key, currentValue, isFresh, def, advanceKeys } = params;

  if (/^[0-9]$/.test(key)) {
    const result = applyDigitToSegment(isFresh ? '' : currentValue, key, def);
    return result
      ? { type: 'digit', value: result.value, complete: result.complete }
      : { type: 'block' };
  }

  if (key === 'Backspace') {
    return currentValue
      ? { type: 'backspaceValue', value: currentValue.slice(0, -1) }
      : { type: 'backspaceRetreat' };
  }

  if (key === 'Delete') return { type: 'clear' };
  if (key === 'ArrowLeft') return { type: 'retreat' };
  if (key === 'ArrowRight' || advanceKeys.includes(key)) {
    return { type: 'advance' };
  }
  if (SEGMENT_NAVIGATION_ALLOWED_KEYS.includes(key)) return { type: 'ignore' };
  return { type: 'block' };
}

export type MonthSegmentKeyAction =
  | { type: 'digit'; value: string; complete: boolean }
  | { type: 'setBuffer'; buffer: string }
  | { type: 'commitFromBuffer'; value: string }
  | { type: 'backspaceBuffer'; buffer: string }
  | { type: 'backspaceValue'; value: string }
  | { type: 'backspaceRetreat' }
  | { type: 'clear' }
  | { type: 'retreat' }
  | { type: 'advance' }
  | { type: 'ignore' }
  | { type: 'block' };

// Resolves what a keystroke should do to the month segment, which (unlike the other
// segments) accepts either digits or letters that narrow down to a month name.
export function resolveMonthSegmentKeyAction(params: {
  key: string;
  monthValue: string;
  monthTextBuffer: string;
  isFresh: boolean;
  monthDef: SegmentDef;
}): MonthSegmentKeyAction {
  const { key, monthValue, monthTextBuffer, isFresh, monthDef } = params;

  if (/^[0-9]$/.test(key)) {
    const result = applyDigitToSegment(
      isFresh ? '' : monthValue,
      key,
      monthDef
    );
    return result
      ? { type: 'digit', value: result.value, complete: result.complete }
      : { type: 'block' };
  }

  if (/^[a-zA-Z]$/.test(key)) {
    const nextBuffer = monthTextBuffer + key;
    const candidates = matchMonthCandidates(nextBuffer);
    if (candidates.length === 0) return { type: 'block' };
    if (candidates.length === 1) {
      return { type: 'commitFromBuffer', value: padDigits(candidates[0], 2) };
    }
    return { type: 'setBuffer', buffer: nextBuffer };
  }

  if (key === 'Backspace') {
    if (monthTextBuffer) {
      return { type: 'backspaceBuffer', buffer: monthTextBuffer.slice(0, -1) };
    }
    if (monthValue) {
      return { type: 'backspaceValue', value: monthValue.slice(0, -1) };
    }
    return { type: 'backspaceRetreat' };
  }

  if (key === 'Delete') return { type: 'clear' };
  if (key === 'ArrowLeft') return { type: 'retreat' };
  if (key === 'ArrowRight' || key === '/' || key === ' ') {
    return { type: 'advance' };
  }
  if (SEGMENT_NAVIGATION_ALLOWED_KEYS.includes(key)) return { type: 'ignore' };
  return { type: 'block' };
}
